import { useRef, useState } from 'react';
import { Popover } from '@/components/common/Popover';
import { formatRelativeTime } from '@/lib/format';
import type { Notebook } from '@/store/notebookStore';

export interface NotebookSwitcherProps {
  readonly notebooks: readonly Notebook[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
  readonly onCreate: () => void;
  readonly onRename: (id: string, name: string) => void;
  readonly onDelete: (id: string) => void;
}

export function NotebookSwitcher({
  notebooks,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}: NotebookSwitcherProps): JSX.Element {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  // Deleting a notebook throws away work, so it takes two clicks.
  const [confirming, setConfirming] = useState<string | null>(null);

  const active = notebooks.find((notebook) => notebook.id === activeId);

  const commitRename = (id: string): void => {
    const name = draft.trim();
    if (name !== '') onRename(id, name);
    setRenaming(null);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="icon-btn max-w-[200px] gap-1.5 px-1.5"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Switch notebook"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="truncate text-xs text-ink">{active?.name ?? 'Notebook'}</span>
        <span className="shrink-0 text-[9px] text-faint" aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        <Popover
          anchorRef={anchorRef}
          onClose={() => {
            setOpen(false);
            setConfirming(null);
            setRenaming(null);
          }}
          width={300}
          role="dialog"
          ariaLabel="Notebooks"
          testId="notebook-switcher"
        >
          <div className="flex items-center gap-2 border-b border-line px-2.5 py-1.5">
            <span className="eyebrow">Notebooks</span>
            <span className="tnum text-[10px] text-faint">{notebooks.length}</span>
            <button
              type="button"
              className="btn ml-auto"
              onClick={() => {
                onCreate();
                setOpen(false);
              }}
            >
              + New
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto" role="list">
            {notebooks.map((notebook) => {
              const isActive = notebook.id === activeId;

              if (renaming === notebook.id) {
                return (
                  <div key={notebook.id} className="border-b border-line px-2.5 py-1.5">
                    <input
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- the
                      // user just chose Rename; anything else needs a second click.
                      autoFocus
                      className="field"
                      aria-label={`Rename ${notebook.name}`}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      onBlur={() => commitRename(notebook.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commitRename(notebook.id);
                        } else if (event.key === 'Escape') {
                          event.preventDefault();
                          setRenaming(null);
                        }
                      }}
                    />
                  </div>
                );
              }

              return (
                <div
                  key={notebook.id}
                  role="listitem"
                  className="group flex items-center gap-1 border-b border-line px-2.5 py-1.5 transition-colors hover:bg-raised"
                  style={{ background: isActive ? 'var(--c-selection)' : undefined }}
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    aria-current={isActive}
                    onClick={() => {
                      onSelect(notebook.id);
                      setOpen(false);
                    }}
                  >
                    <span className="block truncate text-xs text-ink">{notebook.name}</span>
                    <span className="tnum block text-[10px] text-faint">
                      {notebook.cells.length} {notebook.cells.length === 1 ? 'cell' : 'cells'} ·{' '}
                      {formatRelativeTime(notebook.updatedAt)}
                    </span>
                  </button>

                  {confirming === notebook.id ? (
                    <>
                      <span className="text-[10px] text-danger">Delete?</span>
                      <button
                        type="button"
                        className="btn"
                        aria-label={`Confirm delete ${notebook.name}`}
                        onClick={() => {
                          onDelete(notebook.id);
                          setConfirming(null);
                        }}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className="btn"
                        aria-label="Cancel delete"
                        onClick={() => setConfirming(null)}
                      >
                        No
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="icon-btn h-5 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
                        aria-label={`Rename ${notebook.name}`}
                        onClick={() => {
                          setDraft(notebook.name);
                          setRenaming(notebook.id);
                        }}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="icon-btn h-5 opacity-0 transition-opacity hover:!text-danger focus:opacity-100 group-hover:opacity-100"
                        aria-label={`Delete ${notebook.name}`}
                        disabled={notebooks.length <= 1}
                        title={
                          notebooks.length <= 1
                            ? 'The last notebook cannot be deleted.'
                            : `Delete ${notebook.name}`
                        }
                        onClick={() => setConfirming(notebook.id)}
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Popover>
      ) : null}
    </>
  );
}
